// 复制图片到系统剪贴板（多路径兜底）。
// 与对话区 TelegramMenus.vue 的 copyImage 行为保持一致：
//   A 安全上下文 async clipboard 写 PNG（微信/QQ 等原生 IM 认）
//   B contenteditable + 渲染过的 <img> + execCommand 写位图（Chromium 走 CF_DIB）
//   C copy 事件拦截写 text/html（仅 web 应用能收）
//   D 兜底复制 URL 文本
export async function copyImageToClipboard(url) {
  if (!url) return false
  let blob = null
  try {
    const resp = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (!resp.ok) throw new Error('HTTP ' + resp.status)
    blob = await resp.blob()
  } catch (e) {
    console.warn('[copyImage] fetch failed:', e)
  }
  // 路径 A：async clipboard（仅 HTTPS / 安全上下文可用）
  if (blob && window.isSecureContext && window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
    try {
      const pngBlob = blob.type === 'image/png' ? blob : await _imageBlobToPng(blob)
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })])
      console.log('[copyImage] async clipboard OK')
      return true
    } catch (e) {
      console.warn('[copyImage] async clipboard write failed:', e)
    }
  }
  // 路径 B：contenteditable + 已加载渲染的 <img> + selectNode + execCommand
  if (blob) {
    try {
      const ok = await _copyImgAsBitmap(blob)
      if (ok) { console.log('[copyImage] bitmap-copy OK'); return true }
    } catch (e) {
      console.warn('[copyImage] bitmap-copy failed:', e)
    }
    // 路径 C：copy 事件拦截写 HTML（web 应用能收，原生 IM 不行）
    try {
      const dataUrl = await _blobToDataUrl(blob)
      const ok = _copyHtmlViaCopyEvent(`<img src="${dataUrl}">`, '[图片]')
      if (ok) { console.log('[copyImage] html-copy OK (web app only)'); return true }
    } catch (e) {
      console.warn('[copyImage] html-copy failed:', e)
    }
  }
  // 路径 D：兜底复制 URL 文本
  const okText = _copyHtmlViaCopyEvent(null, url)
  if (!okText) console.warn('[copyImage] all paths failed')
  return okText
}

function _copyImgAsBitmap(blob) {
  return new Promise((resolve) => {
    const blobUrl = URL.createObjectURL(blob)
    const div = document.createElement('div')
    div.contentEditable = 'true'
    // 关键：要 viewport 内且实际渲染过；opacity:0/display:none/离屏都会让 Chromium 跳过位图写入
    div.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;overflow:hidden;opacity:0.01;pointer-events:none;z-index:-9999;background:#fff'
    const img = document.createElement('img')
    img.style.cssText = 'display:block;'
    let settled = false
    const finish = (ok) => {
      if (settled) return
      settled = true
      URL.revokeObjectURL(blobUrl)
      try { document.body.removeChild(div) } catch (_) {}
      resolve(ok)
    }
    img.onload = () => {
      // 等两帧 RAF 确保完成 paint
      requestAnimationFrame(() => requestAnimationFrame(() => {
        try {
          div.focus()
          const sel = window.getSelection()
          const range = document.createRange()
          range.selectNode(img)
          sel.removeAllRanges()
          sel.addRange(range)
          let ok = false
          try { ok = document.execCommand('copy') } catch (e) { console.warn('[bitmap-copy] execCommand threw:', e) }
          sel.removeAllRanges()
          finish(ok)
        } catch (e) {
          console.warn('[bitmap-copy] select/copy threw:', e)
          finish(false)
        }
      }))
    }
    img.onerror = (e) => { console.warn('[bitmap-copy] img load failed', e); finish(false) }
    img.src = blobUrl
    div.appendChild(img)
    document.body.appendChild(div)
    setTimeout(() => finish(false), 8000)
  })
}

function _imageBlobToPng(blob) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objUrl = URL.createObjectURL(blob)
    img.onload = () => {
      const c = document.createElement('canvas')
      c.width = img.naturalWidth
      c.height = img.naturalHeight
      c.getContext('2d').drawImage(img, 0, 0)
      c.toBlob(b => { URL.revokeObjectURL(objUrl); b ? resolve(b) : reject(new Error('toBlob failed')) }, 'image/png')
    }
    img.onerror = () => { URL.revokeObjectURL(objUrl); reject(new Error('image load failed')) }
    img.src = objUrl
  })
}

function _blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result)
    r.onerror = () => reject(r.error || new Error('FileReader failed'))
    r.readAsDataURL(blob)
  })
}

// 通用：拦截 copy 事件强写 clipboardData。html=null 则只写 text。
function _copyHtmlViaCopyEvent(html, plainText) {
  const ta = document.createElement('textarea')
  ta.value = plainText || ' '
  ta.setAttribute('readonly', '')
  ta.style.cssText = 'position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:0;opacity:0;'
  document.body.appendChild(ta)
  const handler = (e) => {
    try {
      e.preventDefault()
      if (html) e.clipboardData.setData('text/html', html)
      e.clipboardData.setData('text/plain', plainText || '')
    } catch (err) { console.warn('[clipboardData.setData] failed:', err) }
  }
  document.addEventListener('copy', handler, { once: true, capture: true })
  ta.focus()
  ta.select()
  let ok = false
  try { ok = document.execCommand('copy') } catch (e) { console.warn('[execCommand copy] threw:', e) }
  document.removeEventListener('copy', handler, { capture: true })
  document.body.removeChild(ta)
  return ok
}
